import { newsItems } from '@/mocks/news';
import { workItems } from '@/mocks/works';
import { cultureItems } from '@/mocks/culture';

export function formatDate(date: string) {
  return date.replace(/-/g, '.');
}

export function formatDateCN(date: string) {
  const [y, m, d] = date.split('-');
  return `${y} 年 ${Number(m)} 月 ${Number(d)} 日`;
}

export function getNewsById(id?: string) {
  return newsItems.find((item) => item.id === id);
}

export function getWorkById(id?: string) {
  return workItems.find((item) => item.id === id);
}

export function getCultureById(id?: string) {
  return cultureItems.find((item) => item.id === id);
}

export function getRelatedNews(id: string, limit = 3) {
  const current = getNewsById(id);
  const sameSection = newsItems.filter(
    (item) => item.id !== id && current && item.section === current.section,
  );
  const others = newsItems.filter(
    (item) => item.id !== id && (!current || item.section !== current.section),
  );
  return [...sameSection, ...others].slice(0, limit);
}

export function getRelatedWorks(id: string, limit = 3) {
  const current = getWorkById(id);
  if (!current) return workItems.slice(0, limit);
  const sameType = workItems.filter(
    (item) => item.id !== id && item.type === current.type,
  );
  const sameTag = workItems.filter(
    (item) =>
      item.id !== id &&
      item.type !== current.type &&
      item.tags.some((tag) => current.tags.includes(tag)),
  );
  const rest = workItems.filter(
    (item) =>
      item.id !== id &&
      !sameType.includes(item) &&
      !sameTag.includes(item),
  );
  return [...sameType, ...sameTag, ...rest].slice(0, limit);
}

export function getRelatedCulture(id: string, limit = 3) {
  return cultureItems.filter((item) => item.id !== id).slice(0, limit);
}

export type SearchResultType = 'news' | 'works' | 'culture';

export type SearchResult = {
  id: string;
  type: SearchResultType;
  title: string;
  summary: string;
  date: string;
  cover: string;
  href: string;
  meta: string;
};

const match = (keyword: string, fields: (string | string[] | undefined)[]) => {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return false;
  return fields.some((field) => {
    if (!field) return false;
    if (Array.isArray(field)) {
      return field.some((f) => f.toLowerCase().includes(kw));
    }
    return field.toLowerCase().includes(kw);
  });
};

export function searchContent(keyword: string): SearchResult[] {
  const newsResults: SearchResult[] = newsItems
    .filter((item) =>
      match(keyword, [item.title, item.summary, item.tags, item.content, item.section]),
    )
    .map((item) => ({
      id: item.id,
      type: 'news',
      title: item.title,
      summary: item.summary,
      date: item.date,
      cover: item.cover,
      href: `/detail/news/${item.id}`,
      meta: `${item.section} · ${formatDate(item.date)}`,
    }));

  const workResults: SearchResult[] = workItems
    .filter((item) =>
      match(keyword, [
        item.title,
        item.originalTitle,
        item.intro,
        item.tags,
        item.creator,
        item.content,
      ]),
    )
    .map((item) => ({
      id: item.id,
      type: 'works',
      title: item.title,
      summary: item.intro,
      date: item.updatedAt,
      cover: item.cover,
      href: `/detail/works/${item.id}`,
      meta: `${item.type} · ${item.region} · ${item.year}`,
    }));

  const cultureResults: SearchResult[] = cultureItems
    .filter((item) =>
      match(keyword, [
        item.title,
        item.subtitle,
        item.summary,
        item.tags,
        item.content.flatMap((section) => [section.heading, ...section.paragraphs]),
      ]),
    )
    .map((item) => ({
      id: item.id,
      type: 'culture',
      title: item.title,
      summary: item.summary,
      date: item.date,
      cover: item.cover,
      href: `/detail/culture/${item.id}`,
      meta: `文化专题 · ${formatDate(item.date)}`,
    }));

  return [...newsResults, ...workResults, ...cultureResults];
}